import React, { useState, useMemo } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tooltip,
  CircularProgress,
  Alert,
} from "@mui/material";
import {
  NavigateBefore,
  NavigateNext,
  AccessTime,
  Room,
  Close,
  Today,
  SwapHoriz,
} from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers";
import { useTeacherTimetable } from "../../hooks/queries/useTimetable";
import { useSelector } from "react-redux";
import scheduleService from "../../services/scheduleService";

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

const TeacherTimetable = () => {
  const { user } = useSelector((state) => state.auth);
  const teacherId = user?.teacherId;
  const userRole = user?.role;
  const [selectedWeek, setSelectedWeek] = useState(0);

  // Only fetch teacher timetable when the user actually has a `teacherId`.
  // Do not fall back to schedule endpoints here — admin/hod pages use the
  // dedicated admin timetable components which already call the
  // timetable-generation APIs.
  const { data: apiData, isLoading, error } = useTeacherTimetable(teacherId, { weekOffset: selectedWeek });
  const [requestDialogOpen, setRequestDialogOpen] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [submittingRequest, setSubmittingRequest] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [requestData, setRequestData] = useState({
    reason: "",
    preferredDate: null,
    newStartTime: "",
    newEndTime: "",
  });

  const weekStartLabel = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    const day = (d.getDay() + 6) % 7;
    d.setDate(d.getDate() - day + selectedWeek * 7);
    return d.toLocaleDateString("en-US", { month: "short", day: "2-digit" });
  }, [selectedWeek]);

  // Transform API data into a structured format
  const { timetableData, timeSlots } = useMemo(() => {

    if (!apiData?.data?.data?.slots) {
      return { timetableData: {}, timeSlots: [] };
    }

    const slots = apiData.data.data.slots;
    const uniqueTimes = [...new Set(slots.map((slot) => 
      `${slot.startTime}-${slot.endTime}`
    ))].sort();


    const structuredData = {};
    DAYS.forEach((day) => {
      structuredData[day] = slots
        .filter((slot) => slot.dayOfWeek?.toUpperCase() === day.toUpperCase())
        .sort((a, b) => a.startTime.localeCompare(b.startTime))
        .map((slot) => ({
          time: `${slot.startTime}-${slot.endTime}`,
          subject: slot.subject?.name || "Free Period",
          class: slot.class?.name || "",
          room: slot.room || "",
          type: slot.type || "LECTURE",
          slotData: slot,
        }));
    });

    return { timetableData: structuredData, timeSlots: uniqueTimes };
  }, [apiData]);

  // If the logged-in user is not linked to a Teacher record, show a clearer message.
  if (!teacherId) {
    return (
      <Alert severity="info" sx={{ mt: 2 }}>
        My Timetable is only available for teacher accounts that are linked to a teacher profile.
        {userRole && ` (Current role: ${userRole}).`}{" "}
        As an institute or admin user, please use the Timetable Dashboard and Generate Timetable
        pages to view and manage class timetables.
      </Alert>
    );
  }

  const getSlotColor = (subject) => {
    if (subject === "Free Period") return "grey.100";
    if (subject === "Lunch Break") return "grey.200";
    if (subject === "Meeting") return "warning.light";
    return "primary.light";
  };

  const handleRequestChange = (day, slot) => {
    setSelectedSlot({ day, ...slot });
    setRequestDialogOpen(true);
    setSubmitError(null); // Reset error when opening dialog
  };

  const handleSubmitRequest = async () => {
    if (!selectedSlot || !requestData.reason.trim()) {
      setSubmitError("Please provide a reason for the change request.");
      return;
    }

    if (!selectedSlot.slotData?.id) {
      setSubmitError("Unable to identify the timetable slot. Please try again.");
      return;
    }

    const hasStart = !!requestData.newStartTime;
    const hasEnd = !!requestData.newEndTime;
    if (hasStart !== hasEnd) {
      setSubmitError("Please provide both start and end time.");
      return;
    }
    if (hasStart && hasEnd && requestData.newEndTime <= requestData.newStartTime) {
      setSubmitError("End time must be greater than start time.");
      return;
    }

    setSubmittingRequest(true);
    setSubmitError(null);

    try {
      // Prepare the request data
      const formatDate = (date) => {
        if (!date) return null;
        if (date instanceof Date) {
          return date.toISOString().split('T')[0];
        }
        // If it's already a string in YYYY-MM-DD format
        if (typeof date === 'string' && date.match(/^\d{4}-\d{2}-\d{2}$/)) {
          return date;
        }
        // Fallback
        return null;
      };

      const requestPayload = {
        timeTableSlotId: selectedSlot.slotData?.id,
        changeType: "time_change", // Default to time change, can be made configurable later
        reason: requestData.reason.trim(),
        preferredDate: formatDate(requestData.preferredDate),
        preferredTime: requestData.newStartTime || null,
        newDay: formatDate(requestData.preferredDate)
          ? new Date(formatDate(requestData.preferredDate)).toLocaleDateString("en-US", { weekday: "long" }).toUpperCase()
          : String(selectedSlot?.slotData?.dayOfWeek || selectedSlot?.day || "").toUpperCase() || null,
        newStartTime: requestData.newStartTime || null,
        newEndTime: requestData.newEndTime || null,
      };

      // Call the API
      const response = await scheduleService.createScheduleRequest(requestPayload);

      if (response.status === 201) {
        // Success - close dialog and reset form
        setRequestDialogOpen(false);
        setRequestData({
          reason: "",
          preferredDate: null,
          newStartTime: "",
          newEndTime: "",
        });
        setSelectedSlot(null);
        // You might want to show a success message or refresh the timetable
      }
    } catch (error) {
      console.error("Error submitting schedule change request:", error);
      setSubmitError(
        error.response?.data?.message || 
        "Failed to submit schedule change request. Please try again."
      );
    } finally {
      setSubmittingRequest(false);
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        Failed to load timetable: {error.message}
      </Alert>
    );
  }

  if (!apiData?.data) {
    return (
      <Alert severity="info" sx={{ mt: 2 }}>
        No timetable has been generated yet. Please contact your administrator.
      </Alert>
    );
  }

  const slots = apiData?.data?.data?.slots || [];
  if (!Array.isArray(slots) || slots.length === 0) {
    return (
      <Box>
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
          <Box>
            <Typography variant="h5" fontWeight={600}>
              My Timetable
            </Typography>
            <Typography variant="body2" color="text.secondary">
              View and request schedule changes
            </Typography>
          </Box>
        </Box>
        <Card sx={{ mb: 3 }}>
          <CardContent sx={{ py: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <IconButton onClick={() => setSelectedWeek((w) => w - 1)}>
                <NavigateBefore />
              </IconButton>
              <Typography variant="subtitle1" fontWeight={500}>
                {selectedWeek === 0 ? "Current Week" : `Week of ${weekStartLabel}`}
              </Typography>
              <IconButton onClick={() => setSelectedWeek((w) => w + 1)}>
                <NavigateNext />
              </IconButton>
            </Box>
            <Chip icon={<Today />} label={`Today: ${new Date().toLocaleDateString('en-US', { weekday: 'long' })}`} color="primary" />
          </CardContent>
        </Card>
        <Alert severity="info" sx={{ mt: 2 }}>
          No data found for the selected week.
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={600}>
            My Timetable
          </Typography>
          <Typography variant="body2" color="text.secondary">
            View and request schedule changes
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          {/* <Button variant="outlined" startIcon={<Print />}>
            Print
          </Button>
          <Button variant="outlined" startIcon={<Download />}>
            Download
          </Button> */}
        </Box>
      </Box>

      {/* Week Navigation */}
      <Card sx={{ mb: 3 }}>
        <CardContent
          sx={{
            py: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <IconButton onClick={() => setSelectedWeek((w) => w - 1)}>
              <NavigateBefore />
            </IconButton>
            <Typography variant="subtitle1" fontWeight={500}>
              {selectedWeek === 0 ? "Current Week" : `Week of ${weekStartLabel}`}
            </Typography>
            <IconButton onClick={() => setSelectedWeek((w) => w + 1)}>
              <NavigateNext />
            </IconButton>
          </Box>
          <Chip icon={<Today />} label={`Today: ${new Date().toLocaleDateString('en-US', { weekday: 'long' })}`} color="primary" />
        </CardContent>
      </Card>

      {/* Timetable Grid */}
      <Card sx={{ mb: 3 }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, bgcolor: "grey.100" }}>
                  Time
                </TableCell>
                {DAYS.map((day) => (
                  <TableCell
                    key={day}
                    sx={{
                      fontWeight: 600,
                      bgcolor: "grey.100",
                      textAlign: "center",
                    }}
                  >
                    {day}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {timeSlots.map((time, timeIndex) => (
                <TableRow key={time}>
                  <TableCell sx={{ fontWeight: 500 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <AccessTime
                        sx={{ fontSize: 16, color: "text.secondary" }}
                      />
                      {time}
                    </Box>
                  </TableCell>
                  {DAYS.map((day) => {
                    const slot = timetableData[day]?.find((s) => s.time === time) || {
                      subject: "",
                      class: "",
                      room: "",
                    };
                    return (
                      <TableCell key={day}>
                        {slot.subject && slot.subject !== "Free Period" && (
                          <Tooltip title="Click to request change">
                            <Box
                              onClick={() => handleRequestChange(day, slot)}
                              sx={{
                                bgcolor: getSlotColor(slot.subject),
                                borderRadius: 1,
                                p: 1,
                                cursor: "pointer",
                                "&:hover": { opacity: 0.8 },
                                minHeight: 60,
                              }}
                            >
                              <Typography variant="body2" fontWeight={500}>
                                {slot.subject}
                              </Typography>
                              {slot.class && (
                                <Chip
                                  label={slot.class}
                                  size="small"
                                  sx={{ mt: 0.5, height: 20, fontSize: 10 }}
                                />
                              )}
                              {slot.room && (
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.5,
                                    mt: 0.5,
                                  }}
                                >
                                  <Room sx={{ fontSize: 12 }} />
                                  <Typography variant="caption">
                                    {slot.room}
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          </Tooltip>
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Pending Requests */}
      {/* <Card>
        <CardContent>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            Schedule Change Requests
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Time Slot</TableCell>
                  <TableCell>Reason</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pendingRequests.map((req) => (
                  <TableRow key={req.id}>
                    <TableCell>{req.date}</TableCell>
                    <TableCell>{req.time}</TableCell>
                    <TableCell>{req.reason}</TableCell>
                    <TableCell>
                      <Chip
                        label={req.status}
                        size="small"
                        color={
                          req.status === "approved" ? "success" : "warning"
                        }
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card> */}

      {/* Request Change Dialog */}
      <Dialog
        open={requestDialogOpen}
        onClose={(e, reason) => { if (reason === "backdropClick" || reason === "escapeKeyDown") return; setRequestDialogOpen(false); }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            Request Schedule Change
            <IconButton onClick={() => setRequestDialogOpen(false)}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedSlot && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">
                  Current Slot
                </Typography>
                <Typography variant="body2" fontWeight={500}>
                  {selectedSlot.day} - {selectedSlot.time}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">
                  Subject
                </Typography>
                <Typography variant="body2">
                  {selectedSlot.subject} ({selectedSlot.class})
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <DatePicker
                  label="Preferred Date"
                  value={requestData.preferredDate}
                  onChange={(date) =>
                    setRequestData({ ...requestData, preferredDate: date })
                  }
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Start Time"
                  type="time"
                  value={requestData.newStartTime}
                  onChange={(e) =>
                    setRequestData({
                      ...requestData,
                      newStartTime: e.target.value,
                    })
                  }
                  InputLabelProps={{
                    shrink: true,
                  }}
                  inputProps={{
                    step: 300, // 5 min
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="End Time"
                  type="time"
                  value={requestData.newEndTime}
                  onChange={(e) => setRequestData({ ...requestData, newEndTime: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ step: 300 }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Reason"
                  multiline
                  rows={3}
                  value={requestData.reason}
                  onChange={(e) =>
                    setRequestData({ ...requestData, reason: e.target.value })
                  }
                />
              </Grid>
            </Grid>
          )}
          {submitError && (
            <Grid item xs={12}>
              <Alert severity="error" sx={{ mt: 1 }}>
                {submitError}
              </Alert>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRequestDialogOpen(false)} disabled={submittingRequest}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            startIcon={<SwapHoriz />} 
            onClick={handleSubmitRequest}
            disabled={submittingRequest || !requestData.reason.trim()}
          >
            {submittingRequest ? "Submitting..." : "Submit Request"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TeacherTimetable;
